/**
 * Op sequence coalescing (W1-03 core-model-ops; ARCHITECTURE.md §5.3 gestures and history merges).
 *
 * Ops on different records commute (apply is per record; indexes are derived), so a sequence folds per record:
 *  - consecutive sets on a record collapse (first prev, last next); fields that end where they started drop out;
 *  - ins followed by sets folds into the ins; ins followed by del cancels;
 *  - sets followed by del become one del of the ORIGINAL record; del followed by ins becomes a set (or nothing);
 *  - single ops per singleton and order ops per list collapse like sets; idSeq ops become one (the maximum), last.
 * The result applies identically to the input (same document and indexes) and keeps the first-appearance order of
 * the records. A record whose ops cannot be folded (e.g. a set after a del, which apply rejects) keeps them as is.
 */

import { applyPatch, fieldOf, valueEquals } from './equal';
import type { DelOp, InsOp, Op, OrderOp, SetOp } from './types';
import type { OrderListName, SingletonName, TableName, TableRecords } from '../model/types';

type AnyRec = TableRecords[TableName];
type Patch = Record<string, unknown>;
type RecOp = Extract<Op, { o: 'ins' | 'del' | 'set' }>;

type RecState =
  /** The record existed before and still does; only sets were seen (its full value is unknown). */
  | { s: 'patch'; prev: Patch; next: Patch }
  /** Full knowledge: the record before the sequence and now (null = absent). */
  | { s: 'known'; before: AnyRec | null; after: AnyRec | null }
  /** Not foldable: emitted verbatim. */
  | { s: 'raw'; ops: Op[] };

interface RecFold {
  readonly kind: 'rec';
  readonly tbl: TableName;
  readonly id: AnyRec['id'];
  st: RecState;
}

interface SingleFold {
  readonly kind: 'single';
  readonly key: SingletonName;
  readonly prev: Patch;
  readonly next: Patch;
}

interface OrderFold {
  readonly kind: 'order';
  readonly list: OrderListName;
  readonly prev: readonly number[];
  next: readonly number[];
}

type Fold = RecFold | SingleFold | OrderFold;

function recKey(tbl: TableName, id: number | string): string {
  return typeof id === 'number' ? `${tbl}#${String(id)}` : `${tbl}$${id}`;
}

/** Accumulate a set into prev/next (first prev wins, last next wins). */
function mergeSet(prevAcc: Patch, nextAcc: Patch, prev: object, next: object): void {
  for (const k of Object.keys(next)) {
    if (!Object.prototype.hasOwnProperty.call(prevAcc, k)) prevAcc[k] = fieldOf(prev, k);
    nextAcc[k] = (next as Readonly<Patch>)[k];
  }
}

/** prev/next of the fields that differ between two full records (absent = undefined). */
export function recordDiff(a: object, b: object): { prev: Patch; next: Patch } {
  const prev: Patch = {};
  const next: Patch = {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const av = fieldOf(a, k);
    const bv = fieldOf(b, k);
    if (valueEquals(av, bv)) continue;
    prev[k] = av;
    next[k] = bv;
  }
  return { prev, next };
}

function pruned(prev: Patch, next: Patch): { prev: Patch; next: Patch } | null {
  const p: Patch = {};
  const n: Patch = {};
  let any = false;
  for (const k of Object.keys(next)) {
    if (valueEquals(prev[k], next[k])) continue;
    p[k] = prev[k];
    n[k] = next[k];
    any = true;
  }
  return any ? { prev: p, next: n } : null;
}

function initialState(op: RecOp): RecState {
  switch (op.o) {
    case 'ins':
      return { s: 'known', before: null, after: op.rec };
    case 'del':
      return { s: 'known', before: op.rec, after: null };
    case 'set': {
      const st: RecState = { s: 'patch', prev: {}, next: {} };
      mergeSet(st.prev, st.next, op.prev, op.next);
      return st;
    }
  }
}

/** The next state after one more op, or null when the sequence is not foldable. */
function step(st: Exclude<RecState, { s: 'raw' }>, op: RecOp): RecState | null {
  if (st.s === 'patch') {
    switch (op.o) {
      case 'set':
        mergeSet(st.prev, st.next, op.prev, op.next);
        return st;
      case 'del':
        // `op.rec` is the record with every accumulated `next` applied; restoring `prev` gives the original.
        return { s: 'known', before: applyPatch(op.rec, st.prev), after: null };
      case 'ins':
        return null;
    }
  }
  switch (op.o) {
    case 'set':
      return st.after === null ? null : { s: 'known', before: st.before, after: applyPatch(st.after, op.next) };
    case 'del':
      return st.after === null ? null : { s: 'known', before: st.before, after: null };
    case 'ins':
      return st.after !== null ? null : { s: 'known', before: st.before, after: op.rec };
  }
}

function emitRec(f: RecFold, out: Op[]): void {
  const st = f.st;
  switch (st.s) {
    case 'raw':
      out.push(...st.ops);
      return;
    case 'patch': {
      const p = pruned(st.prev, st.next);
      if (p !== null) out.push({ o: 'set', tbl: f.tbl, id: f.id, prev: p.prev, next: p.next } as SetOp);
      return;
    }
    case 'known': {
      const { before, after } = st;
      if (before === null) {
        if (after !== null) out.push({ o: 'ins', tbl: f.tbl, rec: after } as InsOp);
        return;
      }
      if (after === null) {
        out.push({ o: 'del', tbl: f.tbl, rec: before } as DelOp);
        return;
      }
      const d = recordDiff(before, after);
      if (Object.keys(d.next).length > 0)
        out.push({ o: 'set', tbl: f.tbl, id: f.id, prev: d.prev, next: d.next } as SetOp);
      return;
    }
  }
}

function emit(f: Fold, out: Op[]): void {
  switch (f.kind) {
    case 'rec':
      emitRec(f, out);
      return;
    case 'single': {
      const p = pruned(f.prev, f.next);
      if (p !== null) out.push({ o: 'single', key: f.key, prev: p.prev, next: p.next });
      return;
    }
    case 'order':
      if (!valueEquals(f.prev, f.next))
        out.push({ o: 'order', list: f.list, prev: f.prev, next: f.next } satisfies OrderOp);
      return;
  }
}

export function composeOps(ops: readonly Op[]): Op[] {
  const folds = new Map<string, Fold>();
  let idSeq: number | null = null;

  for (const op of ops) {
    switch (op.o) {
      case 'idSeq':
        idSeq = idSeq === null ? op.next : Math.max(idSeq, op.next);
        break;
      case 'single': {
        const k = `single:${op.key}`;
        let f = folds.get(k) as SingleFold | undefined;
        if (f === undefined) {
          f = { kind: 'single', key: op.key, prev: {}, next: {} };
          folds.set(k, f);
        }
        mergeSet(f.prev, f.next, op.prev, op.next);
        break;
      }
      case 'order': {
        const k = `order:${op.list}`;
        const f = folds.get(k) as OrderFold | undefined;
        if (f === undefined) folds.set(k, { kind: 'order', list: op.list, prev: op.prev, next: op.next });
        else f.next = op.next;
        break;
      }
      case 'ins':
      case 'del':
      case 'set': {
        const id = op.o === 'set' ? op.id : op.rec.id;
        const k = recKey(op.tbl, id);
        const f = folds.get(k) as RecFold | undefined;
        if (f === undefined) {
          folds.set(k, { kind: 'rec', tbl: op.tbl, id, st: initialState(op) });
          break;
        }
        if (f.st.s === 'raw') {
          f.st.ops.push(op);
          break;
        }
        const next = step(f.st, op);
        if (next !== null) {
          f.st = next;
        } else {
          const head: Op[] = [];
          emitRec(f, head);
          head.push(op);
          f.st = { s: 'raw', ops: head };
        }
        break;
      }
    }
  }

  const out: Op[] = [];
  for (const f of folds.values()) emit(f, out);
  if (idSeq !== null) out.push({ o: 'idSeq', next: idSeq });
  return out;
}
